import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import BookCard from "@/components/BookCard";
import NewsletterSignup from "@/components/NewsletterSignup";
import ReviewSpotlight from "@/components/ReviewSpotlight";
import SmartImage from "@/components/SmartImage";
import { ArrowRight, Quote, Sparkles } from "lucide-react";

const ACCENT_BG = {
  terracotta: "bg-terracotta",
  sage: "bg-sage",
  ink: "bg-ink",
};

export default function Home() {
  const [books, setBooks] = useState([]);
  const [classics, setClassics] = useState([]);
  const [trending, setTrending] = useState([]);
  const [collections, setCollections] = useState([]);

  useEffect(() => {
    api.get("/books").then((r) => {
      const all = r.data;
      setBooks(all.slice(0, 6));
      setClassics(all.filter((b) => b.external_url).slice(0, 4));
      // Fetch weekly counts and rank
      api.get("/purchases/counts?window=week").then((c) => {
        const counts = c.data.counts || {};
        const ranked = all
          .map((b) => ({ ...b, _count: counts[b.id] || 0 }))
          .filter((b) => b._count > 0)
          .sort((a, b) => b._count - a._count)
          .slice(0, 4);
        setTrending(ranked);
      }).catch(() => {});
    }).catch(() => {});

    // Fetch top 2 collections for the homepage carousel; then hydrate each with 4 books.
    api.get("/collections").then(async (r) => {
      const top = (r.data || []).slice(0, 2);
      const hydrated = await Promise.all(top.map(async (c) => {
        try {
          const d = await api.get(`/collections/${c.slug}`);
          return { ...c, books: (d.data.books || []).slice(0, 4) };
        } catch { return { ...c, books: [] }; }
      }));
      setCollections(hydrated);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO - asymmetric editorial, warm sun-wash */}
      <section className="relative grain hero-glow max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-24 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 label-eyebrow text-terracotta mb-6 bg-terracotta/10 px-3 py-1.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse" />
              est. 2026 — a small press
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Books worth
              <br />
              <span className="italic text-terracotta relative inline-block">
                re-reading,
                <span aria-hidden="true" className="absolute left-0 -bottom-1 w-full h-2 bg-amber/60 -z-0" style={{ transform: 'skewX(-6deg)' }} />
              </span>
              <br />
              made for screens.
            </h1>
            <p className="mt-8 text-lg text-muted_ink max-w-md leading-relaxed">
              Folio is a digital press for slow fiction, considered essays, and quiet poetry. No
              algorithms. No noise. Just a thoughtfully curated shelf.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/browse" data-testid="hero-browse-btn" className="btn-primary">
                Browse the shelf
              </Link>
              <Link to="/register" data-testid="hero-join-btn" className="btn-secondary">
                Join Folio
              </Link>
            </div>
            <div className="mt-12 flex gap-12 text-sm">
              <div>
                <div className="font-serif text-3xl text-terracotta">{books.length || 6}+</div>
                <div className="label-eyebrow mt-1">Titles</div>
              </div>
              <div>
                <div className="font-serif text-3xl text-terracotta">12k</div>
                <div className="label-eyebrow mt-1">Readers</div>
              </div>
              <div>
                <div className="font-serif text-3xl text-terracotta">4.8</div>
                <div className="label-eyebrow mt-1">Avg rating</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] overflow-hidden book-card-cover">
              <SmartImage
                src="https://images.unsplash.com/photo-1519791883288-dc8bd696e667?auto=format&fit=crop&w=940&h=1175&q=80"
                alt="Open book on linen with warm golden fairy lights"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="hidden md:block absolute -bottom-10 -left-10 w-44 aspect-[3/4] overflow-hidden book-card-cover bg-paper">
              <SmartImage
                src="https://images.unsplash.com/photo-1603289847962-9da9640785e3?w=400&q=80"
                alt="Book cover"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Marquee categories */}
      <section className="border-y border-edge bg-linen overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap py-6">
          {["a", "b"].map((dup) => (
            <div key={dup} className="flex items-center gap-16 px-8 font-serif text-3xl italic text-muted_ink">
              <span>Literary Fiction</span><span>•</span>
              <span>Essays</span><span>•</span>
              <span>Poetry</span><span>•</span>
              <span>Short Stories</span><span>•</span>
              <span>Historical Fiction</span><span>•</span>
              <span>Business</span><span>•</span>
            </div>
          ))}
        </div>
      </section>

      {/* As read by */}
      <section data-testid="as-read-by" className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-6">
        <div className="text-center">
          <div className="label-eyebrow text-muted_ink mb-6">As read by</div>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
            {["The Margins Review", "Longreads", "Public Books", "The Millions", "Electric Literature"].map((n) => (
              <span key={n} className="font-serif text-xl italic text-muted_ink hover:text-ink transition-colors">
                {n}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Books */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="label-eyebrow text-sage mb-3">The current shelf</div>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Recently added</h2>
          </div>
          <Link to="/browse" data-testid="see-all-link" className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] hover:text-terracotta">
            See all <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-8 md:gap-12">
          {books.map((b, i) => <BookCard key={b.id} book={b} index={i} />)}
        </div>
      </section>

      {/* Featured Collections carousel */}
      {collections.length > 0 && (
        <section data-testid="home-collections" className="bg-linen py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-12">
              <div>
                <div className="label-eyebrow text-sage mb-3 flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" /> Curated shelves
                </div>
                <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Where to start</h2>
              </div>
              <Link
                to="/collections"
                data-testid="see-all-collections"
                className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] hover:text-terracotta"
              >
                All collections <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              {collections.map((c) => (
                <Link
                  key={c.slug}
                  to={`/collections/${c.slug}`}
                  data-testid={`home-collection-${c.slug}`}
                  className="group block bg-paper border border-edge overflow-hidden transition-shadow hover:shadow-xl"
                >
                  {c.cover_url && (
                    <div className="aspect-[16/9] overflow-hidden bg-linen">
                      <SmartImage
                        src={c.cover_url}
                        alt=""
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                      />
                    </div>
                  )}
                  <div className="p-6 md:p-8">
                    <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-1 ${ACCENT_BG[c.accent] || "bg-ink"} text-paper`}>
                      <Sparkles className="w-3 h-3" /> {c.book_count} book{c.book_count === 1 ? "" : "s"}
                    </div>
                    <h3 className="font-serif text-3xl mt-3 group-hover:text-terracotta transition-colors">{c.title}</h3>
                    <p className="text-sm text-muted_ink mt-2 line-clamp-2">{c.description}</p>

                    {c.books && c.books.length > 0 && (
                      <div className="mt-6 flex -space-x-3">
                        {c.books.slice(0, 4).map((b) => {
                          const cover = b.cover_url || (b.cover_path ? `${API}/files/${b.cover_path}` : null);
                          return (
                            <div
                              key={b.id}
                              className="w-14 h-20 bg-linen border-2 border-paper overflow-hidden flex-shrink-0"
                              title={b.title}
                            >
                              {cover && <SmartImage src={cover} alt="" className="w-full h-full object-cover" />}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="mt-6 label-eyebrow text-terracotta inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                      Open the shelf <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quote / editorial moment */}
      <section className="bg-ink text-paper py-24 md:py-32 relative grain">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Quote className="w-10 h-10 mx-auto text-terracotta" strokeWidth={1} />
          <blockquote className="font-serif italic text-3xl sm:text-4xl lg:text-5xl leading-tight mt-8">
            "Folio reminds me that reading was, and could be again, an act of attention."
          </blockquote>
          <div className="label-eyebrow text-paper/60 mt-8">— The Margins Review</div>
        </div>
      </section>

      {/* Reader review spotlight */}
      <ReviewSpotlight />

      {/* Classic Canon */}
      {classics.length > 0 && (
        <section data-testid="canon-section" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="label-eyebrow text-sage mb-3">The Canon · Folio Editions</div>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight">
                Classics, quietly reset.
              </h2>
              <p className="text-muted_ink mt-4 text-lg leading-relaxed">
                Books that outlived their century — Austen, Whitman, Shelley — as Folio Editions:
                new covers, thoughtful typography, priced honestly.
              </p>
              <p className="text-sm text-muted_ink mt-6">
                Every classic here is also freely available at Project Gutenberg. You're buying
                the design and the delivery.
              </p>
              <Link to="/browse?filter=classic" className="btn-secondary mt-8 inline-flex">
                Browse the canon
              </Link>
            </div>
            <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-6">
              {classics.map((b, i) => <BookCard key={b.id} book={b} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* Most bought this week */}
      {trending.length > 0 && (
        <section data-testid="trending-section" className="border-y border-edge bg-linen">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-28">
            <div className="mb-12">
              <div className="label-eyebrow text-sage mb-3">Currently on shelves</div>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Most read this week.</h2>
              <p className="text-muted_ink mt-3 max-w-xl">
                What other Folio readers picked up in the last seven days.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
              {trending.map((b, i) => <BookCard key={b.id} book={b} index={i} purchasedCount={b._count} />)}
            </div>
          </div>
        </section>
      )}

      {/* About blurb */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="label-eyebrow text-sage mb-4">The house</div>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">Made the long way.</h2>
        </div>
        <div className="lg:col-span-7 text-lg text-muted_ink leading-relaxed space-y-6">
          <p>
            We publish four to six titles a year. Each is edited slowly, designed deliberately, and
            released as a digital edition you actually want to keep.
          </p>
          <p>
            No subscriptions, no spam, no dark patterns. Buy a book once and it lives on your shelf —
            yours to re-read in any season.
          </p>
        </div>
      </section>

      {/* Newsletter signup */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <NewsletterSignup variant="hero" />
      </section>
    </div>
  );
}
